import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AudioPlayer from './AudioPlayer';
import AudioClipDialog from './AudioClipDialog';

const ManageAudioClips = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [clips, setClips] = useState([]);
  const [filteredClips, setFilteredClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingClip, setEditingClip] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDialogInitialTab, setUploadDialogInitialTab] = useState('library');
  
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    category: '',
    tags: []
  });

  // Categories for dropdown
  const categories = [
    { value: 'technique', label: t('audioClips.categories.technique') },
    { value: 'breathing', label: t('audioClips.categories.breathing') },
    { value: 'pronunciation', label: t('audioClips.categories.pronunciation') },
    { value: 'rhythm', label: t('audioClips.categories.rhythm') },
    { value: 'pitch', label: t('audioClips.categories.pitch') },
    { value: 'example', label: t('audioClips.categories.example') },
    { value: 'exercise', label: t('audioClips.categories.exercise') },
    { value: 'other', label: t('audioClips.categories.other') }
  ];

  useEffect(() => {
    if (isOpen && user?.id) {
      loadClips();
    }
  }, [isOpen, user]);

  // Apply filters whenever clips or filter criteria change
  useEffect(() => {
    let filtered = [...clips];

    // Search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter(clip => 
        clip.name.toLowerCase().includes(search) ||
        (clip.description && clip.description.toLowerCase().includes(search)) ||
        (clip.tags && clip.tags.some(tag => tag.toLowerCase().includes(search)))
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter(clip => clip.category === categoryFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortBy === 'file_size') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });

    setFilteredClips(filtered);
  }, [clips, searchText, categoryFilter, sortBy, sortOrder]);

  const loadClips = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/audio-clips?operation=getAudioClips&userId=${user.id}`
      );
      
      if (response.ok) {
        const clipsData = await response.json();
        setClips(clipsData);
        setFilteredClips(clipsData);
      } else {
        throw new Error(t('audioClips.loadFailed'));
      }
    } catch (err) {
      console.error('Error loading audio clips:', err);
      setError(t('audioClips.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (clip) => {
    setEditingClip(clip);
    setEditFormData({
      name: clip.name,
      description: clip.description || '',
      category: clip.category || '',
      tags: clip.tags || []
    });
  };

  const handleSaveEdit = async () => {
    if (!editFormData.name.trim()) {
      setError(t('audioClips.clipNameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/audio-clips?operation=updateAudioClip&id=${editingClip.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editFormData)
        }
      );
      
      if (response.ok) {
        const updatedClip = await response.json();
        setClips(prevClips => prevClips.map(clip => 
          clip.id === editingClip.id ? updatedClip : clip
        ));
        setEditingClip(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || t('audioClips.failedToUpdateClip'));
      }
    } catch (err) {
      console.error('Error updating clip:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clipId, clipName) => {
    if (!window.confirm(t('audioClips.deleteConfirm', { clipName }))) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/audio-clips?operation=deleteAudioClip&id=${clipId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setClips(clips.filter(clip => clip.id !== clipId));
      } else {
        throw new Error(t('audioClips.deleteFailed'));
      }
    } catch (err) {
      console.error('Error deleting clip:', err);
      setError(t('audioClips.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = (tag) => {
    if (tag.trim() && !editFormData.tags.includes(tag.trim())) {
      setEditFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return t('audioClips.unknown');
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const resetFilters = () => {
    setSearchText('');
    setCategoryFilter('');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const getCategoryLabel = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div 
          className="manage-audio-clips-dialog"
          onClick={e => e.stopPropagation()}
          style={{
            background: '#1f2937',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div style={{
            background: '#111827',
            color: '#ffffff',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid #374151'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              {t('audioClips.manageTitle')}
            </h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button 
                onClick={() => {
                  setUploadDialogInitialTab('upload');
                  setShowUploadDialog(true);
                }}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📁 {t('audioClips.uploadNew')}
              </button>
              <button 
                onClick={() => {
                  setUploadDialogInitialTab('record');
                  setShowUploadDialog(true);
                }}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                🎤 {t('audioClips.recordNew')}
              </button>
              <button 
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Filters */}
          <div style={{
            padding: '16px 24px',
            borderBottom: '1px solid #374151',
            background: '#1f2937',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap',
            flexShrink: 0
          }}>
            <input
              type="text"
              placeholder={t('audioClips.searchClips')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                flex: '1',
                minWidth: '200px',
                padding: '8px 12px',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                fontSize: '14px',
                background: '#374151',
                color: '#f9fafb'
              }}
            />
            
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '150px',
                background: '#374151',
                color: '#f9fafb'
              }}
            >
              <option value="">{t('audioClips.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            <select
              value={`${sortBy}_${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('_');
                setSortBy(field);
                setSortOrder(order);
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '150px',
                background: '#374151',
                color: '#f9fafb'
              }}
            >
              <option value="created_at_desc">{t('audioClips.sortNewestFirst')}</option>
              <option value="created_at_asc">{t('audioClips.sortOldestFirst')}</option>
              <option value="name_asc">{t('audioClips.sortNameAZ')}</option>
              <option value="name_desc">{t('audioClips.sortNameZA')}</option>
              <option value="file_size_desc">{t('audioClips.sortLargestFirst')}</option>
              <option value="file_size_asc">{t('audioClips.sortSmallestFirst')}</option>
            </select>

            {(searchText || categoryFilter || sortBy !== 'created_at' || sortOrder !== 'desc') && (
              <button
                onClick={resetFilters}
                style={{
                  padding: '8px 12px',
                  background: '#4b5563',
                  color: '#f9fafb',
                  border: '1px solid #6b7280',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('audioClips.clearFilters')}
              </button>
            )}

            <div style={{ color: '#9ca3af', fontSize: '14px', marginLeft: 'auto' }}>
              {t('audioClips.clipsCount', { filtered: filteredClips.length, total: clips.length })}
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: '#1f2937' }}>
            {error && (
              <div style={{
                background: '#7f1d1d',
                border: '1px solid #991b1b',
                color: '#fecaca',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                {t('audioClips.loading')}
              </div>
            ) : filteredClips.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎵</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#f3f4f6' }}>
                  {clips.length === 0 ? t('audioClips.noClips') : t('audioClips.noMatching')}
                </h3>
                <p style={{ color: '#9ca3af', marginBottom: '24px' }}>
                  {clips.length === 0 
                    ? t('audioClips.uploadFirstClip') 
                    : t('audioClips.adjustFilters')
                  }
                </p>
                {clips.length === 0 && (
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button
                      onClick={() => {
                        setUploadDialogInitialTab('upload');
                        setShowUploadDialog(true);
                      }}
                      style={{
                        padding: '12px 24px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      📁 {t('audioClips.uploadClip')}
                    </button>
                    <button
                      onClick={() => {
                        setUploadDialogInitialTab('record');
                        setShowUploadDialog(true);
                      }}
                      style={{
                        padding: '12px 24px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      🎤 {t('audioClips.recordClip')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {filteredClips.map(clip => (
                  <div
                    key={clip.id}
                    style={{
                      border: '1px solid #4b5563',
                      borderRadius: '4px',
                      padding: '4px 6px',
                      background: '#374151',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {/* Audio Player */}
                    <div style={{ flexShrink: 0 }}>
                      <AudioPlayer
                        clipName={clip.name}
                        fileUrl={clip.file_url}
                        compact={true}
                        showTitle={false}
                      />
                    </div>
                    
                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1px' }}>
                        <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#f3f4f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>
                          {clip.name}
                        </h3>
                        {clip.category && (
                          <span style={{
                            background: '#4b5563',
                            color: '#d1d5db',
                            padding: '1px 3px',
                            borderRadius: '6px',
                            fontSize: '8px',
                            flexShrink: 0
                          }}>
                            {getCategoryLabel(clip.category)}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', fontSize: '9px', color: '#9ca3af', alignItems: 'center' }}>
                        <span>{formatDate(clip.created_at)}</span>
                        <span>•</span>
                        <span>{formatFileSize(clip.file_size)}</span>
                        {clip.description && (
                          <>
                            <span>•</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>
                              {clip.description}
                            </span>
                          </>
                        )}
                        {clip.tags && clip.tags.length > 0 && (
                          <>
                            <span>•</span>
                            <div style={{ display: 'flex', gap: '1px', flexShrink: 0 }}>
                              {clip.tags.slice(0, 2).map(tag => (
                                <span
                                  key={tag}
                                  style={{
                                    background: '#4b5563',
                                    color: '#d1d5db',
                                    padding: '0px 2px',
                                    borderRadius: '4px',
                                    fontSize: '7px'
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                              {clip.tags.length > 2 && (
                                <span style={{ fontSize: '7px', color: '#6b7280' }}>+{clip.tags.length - 2}</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleEdit(clip)}
                        style={{
                          padding: '1px 4px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2px',
                          fontSize: '9px',
                          cursor: 'pointer'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(clip.id, clip.name)}
                        style={{
                          padding: '1px 4px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2px',
                          fontSize: '9px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      {editingClip && (
        <div className="modal-overlay" onClick={() => setEditingClip(null)}>
          <div 
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1f2937',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              border: '1px solid #374151'
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#f3f4f6' }}>
              {t('audioClips.editClip')}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#d1d5db' }}>
                {t('audioClips.name')} *
              </label>
              <input
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#374151',
                  color: '#f9fafb'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#d1d5db' }}>
                {t('audioClips.description')}
              </label>
              <textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                  background: '#374151',
                  color: '#f9fafb'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#d1d5db' }}>
                {t('audioClips.category')}
              </label>
              <select
                value={editFormData.category}
                onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#374151',
                  color: '#f9fafb'
                }}
              >
                <option value="">{t('audioClips.noCategory')}</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#d1d5db' }}>
                {t('audioClips.tags')}
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder={t('audioClips.addTagPlaceholder')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTag(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#374151',
                    color: '#f9fafb'
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = e.target.previousElementSibling;
                    handleAddTag(input.value);
                    input.value = '';
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {t('audioClips.addTag')}
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {editFormData.tags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      background: '#4b5563',
                      color: '#d1d5db',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#9ca3af',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setEditingClip(null)}
                style={{
                  padding: '10px 20px',
                  background: '#4b5563',
                  color: '#f9fafb',
                  border: '1px solid #6b7280',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={loading || !editFormData.name.trim()}
                style={{
                  padding: '10px 20px',
                  background: loading || !editFormData.name.trim() ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading || !editFormData.name.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? t('audioClips.saving') : t('audioClips.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <AudioClipDialog
        isOpen={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onClipSelected={() => {
          setShowUploadDialog(false);
          loadClips(); // Refresh the list
        }}
        initialTab={uploadDialogInitialTab}
      />
    </>
  );
};

export default ManageAudioClips;